'use client';

import { c as _c } from "react/compiler-runtime";
import { useWeb3Context } from '@/contexts/Web3Context';
import { useBorrowPositionsTableData } from '@/hooks/positions/useBorrowPositionsTableData';
import { useDashboardEarnPositionsTableData } from '@/hooks/positions/useDashboardEarnPositionsTableData';
import { LEND_POSITIONS_DEFAULT_ORDER_BY, useLendPositionsTableData } from '@/hooks/positions/useLendPositionsTableData';

/**
 * Total number of positions (earn + borrow + lend) held by the *connected*
 * wallet.
 *
 * Used to badge the Portfolio nav tab. It queries with the Portfolio tables'
 * keys (the lend table's default sort), which warms and reuses the
 * same cache (opening the Portfolio finds the data already loaded, no refetch).
 *
 * Returns `undefined` whenever the count isn't a trustworthy settled total
 * (disconnected, loading, mid account-switch, or on error), distinct from a
 * connected wallet that genuinely holds 0. Callers treat `undefined` as "no
 * badge" and omit it from click analytics (logging 0 there would conflate
 * "unknown" with "holds nothing").
 *
 * The badge is mounted app-wide (NavBar/MobileNavBar/MenuDropdown), so it passes
 * `refetchInterval: false` to opt out of the tables' 10s polling — it only needs
 * to warm/share the cache, not keep a count fresh to the second on every route.
 * The Portfolio page's own observer reinstates the 10s poll when actually open.
 *
 * Note: the count is always the connected wallet's own, even while viewing
 * another address's Portfolio at `/portfolio/0xOther`.
 */
export const useUserPositionsCount = () => {
  const $ = _c(6);
  const {
    account
  } = useWeb3Context();
  const accountAddress = account.isConnected ? account.address : undefined;
  let t0;
  if ($[0] !== accountAddress) {
    t0 = {
      accountAddress,
      refetchInterval: false
    };
    $[0] = accountAddress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    countTotal: earnCount,
    loading: earnLoading,
    isPlaceholderData: earnPlaceholder,
    error: earnError
  } = useDashboardEarnPositionsTableData(t0);
  let t1;
  if ($[2] !== accountAddress) {
    t1 = {
      accountAddress,
      refetchInterval: false
    };
    $[2] = accountAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    countTotal: borrowCount,
    loading: borrowLoading,
    isPlaceholderData: borrowPlaceholder,
    partial: borrowPartial,
    error: borrowError
  } = useBorrowPositionsTableData(t1);
  let t2;
  if ($[4] !== accountAddress) {
    t2 = {
      accountAddress,
      orderBy: LEND_POSITIONS_DEFAULT_ORDER_BY,
      orderDirection: "desc",
      refetchInterval: false
    };
    $[4] = accountAddress;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    countTotal: lendCount,
    loading: lendLoading,
    isPlaceholderData: lendPlaceholder,
    partial: lendPartial,
    error: lendError
  } = useLendPositionsTableData(t2);
  if (!accountAddress) {
    return;
  }
  if (earnLoading || borrowLoading || lendLoading || earnPlaceholder || borrowPlaceholder || lendPlaceholder || borrowPartial || lendPartial || earnError || borrowError || lendError) {
    return;
  }
  return earnCount + borrowCount + lendCount;
};